<template>
  <div class="modal fade" :id="modalId" data-backdrop="static" data-keyboard="false" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-body">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <span class="badge badge-soft-success text-uppercase badge font-12px bg-primary-blink text-white">
              Invoice Generated
            </span>
            <button type="button" class="btn btn-light position-relative p-0 avatar-xs rounded-circle pull-right close-modal"
                    data-bs-dismiss="modal" aria-label="Close">
              <span class="avatar-title bg-transparent text-reset font-18px">
                <i class="bx bx-x"></i>
              </span>
            </button>
          </div>

          <!-- Invoice Display Content -->
          <div class="text-center mb-4">
            <div class="success-image mb-3">
              <img src="assets/images/bills.svg" height="150" alt="Invoice">
            </div>
            <h4 class="text-black fw-bold">{{ message }}</h4>
            <p class="text-muted">The invoice has been created successfully. You can print the invoice or close this window.</p>
          </div>

          <!-- Invoice Details -->
          <div class="border rounded p-4 mb-4">
            <h5 class="text-uppercase mb-3">Invoice Details</h5>
            <div class="row">
              <div class="col-md-6 mb-2">
                <div class="text-muted small">Invoice Number</div>
                <div class="text-black fw-semibold">{{ localBillDetails.billNo || 'N/A' }}</div>
              </div>
              <div class="col-md-6 mb-2">
                <div class="text-muted small">Invoice Date</div>
                <div class="text-black fw-semibold">{{ formatDate(localBillDetails.dateCreated) }}</div>
              </div>
              <div class="col-md-6 mb-2">
                <div class="text-muted small">Unit Number</div>
                <div class="text-black fw-semibold">{{ localUnit.unitNo || 'N/A' }}</div>
              </div>
              <div class="col-md-6 mb-2">
                <div class="text-muted small">Tenant Name</div>
                <div class="text-black fw-semibold">{{ localBillDetails.names || localUnit.occupants || 'N/A' }}</div>
              </div>
              <div class="col-md-6 mb-2">
                <div class="text-muted small">Description</div>
                <div class="text-black fw-semibold">{{ localBillDetails.feeDescription || 'N/A' }}</div>
              </div>
              <div class="col-md-6 mb-2">
                <div class="text-muted small">Payment Code</div>
                <div class="text-black fw-semibold">{{ localBillDetails.billNo || 'N/A' }}</div>
              </div>
            </div>

            <hr class="my-3">

            <!-- Amount Details -->
            <div class="row">
              <div class="col-md-6 mb-2">
                <div class="text-muted small">Invoice Amount</div>
                <div class="text-success fw-bold h5 mb-0">KES {{ numberFormat(localBillDetails.detailAmount || localBillDetails.billBalance) }}</div>
              </div>
              <div class="col-md-6 mb-2">
                <div class="text-muted small">Amount Paid</div>
                <div class="text-info fw-semibold">KES {{ numberFormat(localBillDetails.receiptAmount || 0) }}</div>
              </div>
              <div class="col-md-6 mb-2">
                <div class="text-muted small">Balance Due</div>
                <div class="text-danger fw-bold h5 mb-0">KES {{ numberFormat(localBillDetails.billBalance) }}</div>
              </div>
              <div class="col-md-6 mb-2">
                <div class="text-muted small">Status</div>
                <span :class="['badge', getStatusBadge(localBillDetails.status)]">
                  {{ localBillDetails.status || 'UNPAID' }}
                </span>
              </div>
            </div>

            <!-- Additional Info if available -->
            <div v-if="localBillDetails.zone || localBillDetails.wardName" class="mt-3 pt-3 border-top">
              <div class="row">
                <div class="col-md-6 mb-2" v-if="localBillDetails.zone">
                  <div class="text-muted small">Zone</div>
                  <div class="text-black fw-semibold">{{ localBillDetails.zone }}</div>
                </div>
                <div class="col-md-6 mb-2" v-if="localBillDetails.wardName">
                  <div class="text-muted small">Ward</div>
                  <div class="text-black fw-semibold">{{ localBillDetails.wardName }}</div>
                </div>
                <div class="col-md-6 mb-2" v-if="localBillDetails.subCountyName">
                  <div class="text-muted small">Sub County</div>
                  <div class="text-black fw-semibold">{{ localBillDetails.subCountyName }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="d-flex gap-2 justify-content-end">
            <button @click="printBill" type="button" class="btn btn-primary">
              <i class="mdi mdi-printer font-size-16 align-middle me-2"></i>
              Print Invoice
            </button>
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              <i class="mdi mdi-close font-size-16 align-middle me-2"></i>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "InvoiceDisplayModal",
  props: {
    modalId: {
      type: String,
      default: "invoice-display-modal"
    }
  },
  data() {
    return {
      localBillDetails: {},
      localBillInfo: [],
      localUnit: {},
      message: "Invoice Generated Successfully"
    };
  },
  methods: {
    initializeWithBillData(data) {
      console.log('Initializing InvoiceDisplayModal with data:', data);

      // Initialize with bill data
      this.localBillDetails = data.billDetails || {};
      this.localBillInfo = data.billInfo || [];
      this.localUnit = data.unit || {};

      this.message = "Invoice Generated Successfully";

      console.log('Invoice data loaded:', {
        billNo: this.localBillDetails.billNo,
        amount: this.localBillDetails.billBalance,
        unit: this.localUnit.unitNo
      });
    },

    numberFormat(num) {
      const formatter = new Intl.NumberFormat("en-GB");
      return formatter.format(num || 0);
    },

    formatDate(dateStr) {
      if (!dateStr) return "N/A";

      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "N/A";

      const options = {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      };

      return date.toLocaleDateString('en-US', options);
    },

    getStatusBadge(status) {
      switch(status) {
        case 'PAID':
          return 'bg-success';
        case 'UNPAID':
          return 'bg-danger';
        case 'PARTIAL':
          return 'bg-warning';
        default:
          return 'bg-secondary';
      }
    },

    printBill() {
      if (!this.localBillDetails.billNo) {
        alert("No invoice to print");
        return;
      }

      localStorage['params'] = JSON.stringify({
        billNo: this.localBillDetails.billNo
      });

      const routeData = this.$router.resolve({ name: 'bill' });
      window.open(routeData.href, '_blank');
    }
  }
};
</script>

<style scoped>
.success-image {
  display: inline-block;
}
</style>